import type { InboxFacet } from '@chat-emp/shared';

import { includesFold } from '../util/text.js';

/**
 * One selectable line in the job filter: a standalone posting, a sub-role, or a
 * parent job's own self-row. Every checkbox is exactly one job — a parent is
 * never a shorthand for its children.
 */
export interface JobLeaf {
  id: string;
  title: string;
  /** Where the posting runs. How two postings of one title are told apart. */
  location?: string;
  /** Conversations bound to this exact job, from the facet count. */
  count: number;
  /**
   * A parent that receives no applicants of its own still appears (to carry its
   * sub-roles), but its own checkbox would match nothing — so it renders as a
   * header rather than a checkbox.
   */
  selectable: boolean;
}

/**
 * A top-level entry in the job filter. AJT postings form a two-level tree: a
 * standalone job (`children` empty) or a parent job with sub-roles.
 */
export interface JobTreeNode {
  parent: JobLeaf;
  children: JobLeaf[];
  /** Own count plus every sub-role's — the number on the collapsed header. */
  familyCount: number;
  kind: 'job' | 'parent';
}

/**
 * `String()` on both sides: titles originate in an upstream ES document, where
 * the same mapping can yield a string, an array or a number. `localeCompare`
 * only exists on strings, so a non-string *receiver* throws — out of a render,
 * taking the panel with it.
 */
const byTitle = (a: JobLeaf, b: JobLeaf): number => String(a.title).localeCompare(String(b.title));

/**
 * The job filter's structure, derived from the `by: 'job'` facets being
 * rendered. The facets decide what exists — nothing appears that has no
 * conversations — and `parent_key` decides only the shape.
 *
 * A parent named by a sub-role but holding no chats of its own has no facet, so
 * it is synthesised as a non-selectable header labelled from any child's
 * `parent_label`, falling back to its id.
 *
 * Two levels only: a facet sits under the parent it names, and nothing
 * recurses. AJT postings are two-level, and a cycle in the data must not hang a
 * render.
 *
 * Sorted by title, not by count or activity: an order that shifts as new
 * messages land would reshuffle rows under the cursor while the panel is open.
 */
export function buildJobTree(facets: InboxFacet[]): JobTreeNode[] {
  const leaves = new Map<string, JobLeaf>();
  const childrenOf = new Map<string, string[]>();
  const parentTitles = new Map<string, string>();

  for (const facet of facets) {
    leaves.set(facet.key, {
      id: facet.key,
      title: facet.label || facet.key,
      ...(facet.location ? { location: facet.location } : {}),
      count: facet.count,
      selectable: true,
    });
    const parentKey = facet.parent_key;
    if (!parentKey) continue;
    const siblings = childrenOf.get(parentKey);
    if (siblings) siblings.push(facet.key);
    else childrenOf.set(parentKey, [facet.key]);
    if (facet.parent_label) parentTitles.set(parentKey, facet.parent_label);
  }

  // A parent named by a child but absent from the facets: a header only.
  const header = (id: string): JobLeaf =>
    leaves.get(id) ?? {
      id,
      title: parentTitles.get(id) ?? id,
      count: 0,
      selectable: false,
    };

  const nodes: JobTreeNode[] = [];
  const placed = new Set<string>();

  for (const [parentKey, childKeys] of childrenOf) {
    const parent = header(parentKey);
    const children = childKeys
      .map((key) => leaves.get(key))
      .filter((leaf): leaf is JobLeaf => leaf !== undefined)
      .sort(byTitle);
    nodes.push({
      parent,
      children,
      familyCount: parent.count + children.reduce((sum, child) => sum + child.count, 0),
      kind: 'parent',
    });
    placed.add(parentKey);
    for (const key of childKeys) placed.add(key);
  }

  for (const leaf of leaves.values()) {
    if (placed.has(leaf.id)) continue;
    nodes.push({ parent: leaf, children: [], familyCount: leaf.count, kind: 'job' });
  }

  return nodes.sort((a, b) => byTitle(a.parent, b.parent));
}

/**
 * The tree cut to a search. A family whose NAME matches keeps every role inside
 * it; one matched only through a place or a sub-role keeps just the rows that
 * hit, and `opened` names it so the hit is not left behind a shut header. A
 * ticked job always hits. A returned parent's `selectable` means "show its
 * self-row": a parent that missed is put away as a header only.
 */
export function searchJobTree(
  tree: JobTreeNode[],
  needle: string,
  isSelected: (id: string) => boolean,
): { nodes: JobTreeNode[]; opened: Set<string> } {
  if (needle === '') return { nodes: tree, opened: new Set() };
  const titleHit = (leaf: JobLeaf) => includesFold(leaf.title, needle);
  const hit = (leaf: JobLeaf) =>
    titleHit(leaf) || includesFold(leaf.location, needle) || isSelected(leaf.id);

  const opened = new Set<string>();
  const nodes = tree.flatMap((node) => {
    if (node.kind !== 'parent') return hit(node.parent) ? [node] : [];
    const children = node.children.filter(hit);
    if (children.length > 0) opened.add(node.parent.id);
    if (titleHit(node.parent)) return [node];
    const parent = { ...node.parent, selectable: node.parent.selectable && hit(node.parent) };
    if (!parent.selectable && children.length === 0) return [];
    opened.add(node.parent.id);
    return [{ ...node, parent, children }];
  });
  return { nodes, opened };
}

/** Every job with a checkbox, inside shut families too — what Select all takes. */
export function selectableIds(tree: JobTreeNode[]): string[] {
  return tree.flatMap((node) => [
    ...(node.parent.selectable ? [node.parent.id] : []),
    ...node.children.filter((child) => child.selectable).map((child) => child.id),
  ]);
}
